import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { JobSourceTab } from '@/entities/media-campaign';

/** Matches the server's cap on one read; the dialog refuses to tick more. */
export const MAX_TABS_PER_READ = 10;

export interface TabPickerDialogProps {
  open: boolean;
  tabs: JobSourceTab[];
  isSubmitting: boolean;
  onConfirm: (sheets: string[]) => void;
  onClose: () => void;
}

/**
 * Which tabs of a client workbook hold the plan.
 *
 * Every export looked at kept retired revisions beside the live plan, some hidden, some
 * named as old; the server ticks what looks live and this dialog lets the planner correct
 * it before a read spends minutes and a model budget on the wrong tabs. Hidden tabs are
 * listed collapsed: they are nearly always retired, but a planner who knows otherwise can
 * still reach them.
 */
export function TabPickerDialog({
  open,
  tabs,
  isSubmitting,
  onConfirm,
  onClose,
}: TabPickerDialogProps) {
  const { t } = useTranslation('projects');
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(tabs.filter((tab) => tab.suggested).map((tab) => tab.name)),
  );
  const [showHidden, setShowHidden] = useState(false);

  const visible = tabs.filter((tab) => !tab.hidden);
  const hidden = tabs.filter((tab) => tab.hidden);
  const atCap = picked.size >= MAX_TABS_PER_READ;

  const toggle = (name: string, on: boolean) => {
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });
  };

  const row = (tab: JobSourceTab) => {
    const checked = picked.has(tab.name);
    return (
      <label
        key={tab.name}
        className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-1.5 hover:bg-surface-hover"
        data-testid="tab-picker-row"
      >
        <Checkbox
          checked={checked}
          disabled={isSubmitting || (!checked && atCap)}
          onCheckedChange={(next) => toggle(tab.name, next === true)}
          aria-label={tab.name}
        />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm">{tab.name}</span>
          <span className="text-foreground-subtle text-xs">
            {t('deliveryJobCreate.tabPicker.rowsAndColumns', {
              rows: tab.filledRows,
              columns: tab.columnCount,
            })}
          </span>
        </span>
      </label>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="w-[min(calc(100vw-2rem),34rem)]" data-testid="tab-picker-dialog">
        <DialogHeader>
          <DialogTitle>{t('deliveryJobCreate.tabPicker.whichTabs')}</DialogTitle>
          <DialogDescription>{t('deliveryJobCreate.tabPicker.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto">
          {visible.map(row)}
          {hidden.length > 0 && (
            <>
              <button
                type="button"
                className="text-foreground-subtle mt-2 self-start px-2 text-xs underline-offset-2 hover:underline"
                onClick={() => setShowHidden((current) => !current)}
                data-testid="tab-picker-toggle-hidden"
              >
                {showHidden
                  ? t('deliveryJobCreate.tabPicker.hideHiddenTabs', { count: hidden.length })
                  : t('deliveryJobCreate.tabPicker.showHiddenTabs', { count: hidden.length })}
              </button>
              {showHidden && hidden.map(row)}
            </>
          )}
        </div>

        {atCap && (
          <p className="text-foreground-subtle text-xs" data-testid="tab-picker-cap">
            {t('deliveryJobCreate.tabPicker.atMost', { count: MAX_TABS_PER_READ })}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" size="default" onClick={onClose} disabled={isSubmitting}>
            {t('deliveryJobCreate.tabPicker.cancel')}
          </Button>
          <Button
            size="default"
            onClick={() =>
              onConfirm(tabs.filter((tab) => picked.has(tab.name)).map((tab) => tab.name))
            }
            disabled={picked.size === 0 || picked.size > MAX_TABS_PER_READ || isSubmitting}
            data-testid="tab-picker-confirm"
          >
            {isSubmitting
              ? t('deliveryJobCreate.tabPicker.queueing')
              : t('deliveryJobCreate.tabPicker.readTabs', { count: picked.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
